import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, realtimeDb } from '@/lib/firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, createUserWithEmailAndPassword } from 'firebase/auth';
import { ref, get, set } from 'firebase/database';


const AuthContext = createContext(null);

export const SUPERADMIN_EMAIL = 'mtruiz@consulogic-gt.com';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserRole = async (currentUser) => {
    if (!currentUser) return;

    if (currentUser.email === SUPERADMIN_EMAIL) {
      setUserRole('admin');
      return;
    }

    try {
      const userRef = ref(realtimeDb, `users/${currentUser.uid}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        setUserRole(snapshot.val().role || 'team');
      } else {
        const defaultRole = 'team';
        await set(userRef, {
          email: currentUser.email,
          role: defaultRole,
          createdAt: new Date().toISOString()
        });
        setUserRole(defaultRole);
      }
    } catch (err) {
      console.error("AuthContext - Error fetching role:", err);
      setUserRole('team');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      try {
        if (currentUser) {
          console.log(`[${new Date().toISOString()}] Auth State: Logged IN`, currentUser.email);
          await fetchUserRole(currentUser);
        } else {
          console.log(`[${new Date().toISOString()}] Auth State: Logged OUT`);
          setUserRole(null);
        }
        setUser(currentUser);
      } catch (err) {
        console.error("AuthContext - State Change Error:", err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const signup = async (email, password) => {
    try {
      return await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Signup error:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const isAdmin = userRole === 'admin';
  const isTeam = userRole === 'team' || isAdmin;

  return (
    <AuthContext.Provider value={{ 
      user, 
      userRole, 
      isAdmin, 
      isTeam, 
      login, 
      signup, 
      logout, 
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
